import {
  type QueryClient,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { useFlag } from "@unleash/proxy-client-react";
import { type StaticDecode, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { ReadonlyObject } from "@/shared/schema/schema.ts";
import { httpChecked, queryClient } from "@/shared/util/fetch.tsx";

const settingsSchema = ReadonlyObject({
  customerDescriptions: Type.Boolean(),
  skillSuggestions: Type.Boolean(),
  writingAssistant: Type.Boolean(),
  similarProfiles: Type.Boolean(),
  cvParsing: Type.Boolean(),
  rpParsing: Type.Boolean(),
  proposalAssistant: Type.Boolean(),
});
export type AiSettings = StaticDecode<typeof settingsSchema>;

const SettingsChecker = TypeCompiler.Compile(settingsSchema);

const queries = {
  fetch: queryOptions({
    queryKey: ["ai-settings", "data"] as const,
    queryFn: ({ signal }) =>
      httpChecked.get("/ai-settings", SettingsChecker, { signal }),
  }),
};

function useAiSettings() {
  const { data } = useQuery({ ...queries.fetch, staleTime: 60_000 });

  return data;
}

export function useIsWritingAssistantEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.writingAssistant;
}

export function useIsSimilarProfilesEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.similarProfiles;
}

export function useIsAiCvParsingEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.cvParsing;
}

export function useIsSkillSuggestionsEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.skillSuggestions;
}

export function useIsCustomerDescriptionsEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.customerDescriptions;
}

export function useIsProposalAssistantEnabled() {
  const settings = useAiSettings();

  if (!settings) {
    return false;
  }

  return settings.proposalAssistant;
}

export async function prefetchAiSettings(queryClient: QueryClient) {
  await queryClient.prefetchQuery({ ...queries.fetch, staleTime: 60_000 });
}

export async function invalidateAiSettings() {
  await queryClient.invalidateQueries(queries.fetch);
}

export function useIsAiProjectParsingEnabled() {
  const settings = useAiSettings();
  const projectParsingFlagEnabled = useFlag("ai-parsing-rp");

  if (!settings || !projectParsingFlagEnabled) {
    return false;
  }

  return settings.rpParsing;
}
